import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { findItemByPath } from "@/config/modules";
import { FORMS } from "@/config/forms";
import { IMPORTS } from "@/config/imports";
import { ImportDialog } from "@/components/common/ImportDialog";
import { DuplicatesDialog } from "@/components/common/DuplicatesDialog";
import DeletePurchaseBillDialog from "@/components/purchase/DeletePurchaseBillDialog";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { DetailDrawer } from "@/components/common/DetailDrawer";
import { EntityFormDialog } from "@/components/common/EntityFormDialog";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { get } from "@/lib/format";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, Plus, FileSpreadsheet, GitMerge, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ResourceListPage({ itemOverride }) {
  const location = useLocation();
  const item = itemOverride || findItemByPath(location.pathname);
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, isError, error, refetch } = useResource(item?.endpoint, {
    dataPath: item?.dataPath,
  });

  if (!item) return <ErrorState message="Unknown module." />;

  const formCfg = FORMS[item.path];
  const importCfg = IMPORTS[item.path];
  const refetchList = () => queryClient.invalidateQueries({ queryKey: ["resource", item.endpoint] });

  let rows = Array.isArray(data) ? data : [];
  if (item.clientFilter) {
    const { field, value } = item.clientFilter;
    rows = rows.filter((r) => String(get(r, field) || "").toLowerCase() === String(value).toLowerCase());
  }

  const openRow = (row) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const openCreate = () => {
    setFormMode("create");
    setEditRow(null);
    setFormOpen(true);
  };

  const openEdit = () => {
    setFormMode("edit");
    setEditRow(selected);
    setDrawerOpen(false);
    setFormOpen(true);
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (formMode === "edit") {
        const id = editRow?.[item.idKey] || editRow?.id;
        await api.put(`${item.endpoint}/${id}`, payload);
        toast.success(`${formCfg.singular} updated`);
      } else {
        await api.post(item.endpoint, payload);
        toast.success(`${formCfg.singular} created`);
      }
      setFormOpen(false);
      queryClient.invalidateQueries({ queryKey: ["resource", item.endpoint] });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "SUPPLIER_ALREADY_EXISTS") {
        toast.error(detail.message || "Supplier already exists", {
          duration: 8000,
          description: `Existing: ${detail.existingName} (${detail.existingSupplierCode})${detail.existingGstin ? ` · GSTIN ${detail.existingGstin}` : " · no GSTIN"}`,
          action: {
            label: "Use Existing Supplier",
            onClick: async () => {
              try {
                const res = await api.get(`${item.endpoint}/${detail.existingSupplierId}`);
                setSelected(res.data); setFormOpen(false); setDrawerOpen(true);
              } catch { /* ignore */ }
            },
          },
        });
      } else {
        toast.error(apiErrorMessage(err, "Save failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    if (!item.exportEndpoint) return;
    setExporting(true);
    try {
      const res = await api.get(item.exportEndpoint, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${item.testId}-export.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  const canDelete = item.deleteEndpoint && ["super_admin", ...(item.deleteRoles || [])].includes(role);
  const isPurchaseBill = item.deleteEndpoint === "/purchase-bills";
  const doDelete = async () => {
    setDeleting(true);
    try {
      const id = get(selected, item.idKey || "id");
      await api.delete(`${item.deleteEndpoint}/${id}?reason=${encodeURIComponent(delReason)}`);
      toast.success("Invoice deleted.");
      setDelOpen(false); setDrawerOpen(false); setDelReason(""); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setDeleting(false); }
  };

  return renderPage();

  function renderPage() {
  return (
    <>
    {mainContent()}
    <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
      <AlertDialogContent className="bg-white">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Purchase Invoice?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes invoice <b>{get(selected, "supplierInvoiceNumber") || get(selected, "billNo")}</b>
            {" "}({get(selected, "supplierName")}). A deletion audit record is kept. Blocked if stock/GRN/payments are linked.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-1">
          <input data-testid="pb-delete-reason" placeholder="Reason (optional)" value={delReason}
            onChange={(e) => setDelReason(e.target.value)} className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="pb-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="pb-delete-confirm" disabled={deleting} onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
  }

  function mainContent() {
  return (
    <div className="space-y-6">
      <PageHeader title={item.label} subtitle="Live data from the shared Freshezy ERP backend">
        {importCfg && (
          <>
            <Button data-testid={`btn-import-${item.testId}`} variant="outline" onClick={() => setImportOpen(true)} className="gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Import / Template
            </Button>
            {importCfg.duplicatesEndpoint && (
              <Button data-testid={`btn-duplicates-${item.testId}`} variant="outline" onClick={() => setDupOpen(true)} className="gap-2">
                <GitMerge className="h-4 w-4" /> Find Duplicates
              </Button>
            )}
          </>
        )}
        {formCfg && (
          <Button data-testid={`btn-new-${item.testId}`} onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800">
            <Plus className="h-4 w-4" /> New {formCfg.singular}
          </Button>
        )}
      </PageHeader>

      {item.mobileNote && (
        <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-3 text-sm text-sky-800">
          <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Detailed <strong>{item.label}</strong> checklists (photos, signatures, checkpoints) are captured on the
            shop-floor mobile app. Records below are read from the same shared backend.
          </span>
        </div>
      )}

      {isLoading ? (
        <LoadingState label={`Loading ${item.label.toLowerCase()}…`} />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={refetch} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={`No ${item.label.toLowerCase()} yet`}
          message="There are no records in the shared ERP for this module right now. New entries made on web or mobile will appear here."
          action={formCfg ? (
            <Button data-testid={`empty-new-${item.testId}`} onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800">
              <Plus className="h-4 w-4" /> New {formCfg.singular}
            </Button>
          ) : undefined}
        />
      ) : (
        <DataTable
          rows={rows}
          columns={item.columns}
          searchKeys={item.searchKeys}
          filters={item.filters}
          onRowClick={openRow}
          onExport={item.exportEndpoint ? handleExport : undefined}
          exporting={exporting}
          testIdPrefix={`${item.testId}-row`}
        />
      )}

      <DetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={`${item.label} detail`}
        row={selected}
        onEdit={formCfg?.editFields ? openEdit : undefined}
        actions={canDelete ? (
          isPurchaseBill ? (
            <DeletePurchaseBillDialog
              billId={get(selected, item.idKey || "id")}
              invoiceNumber={get(selected, "supplierInvoiceNumber") || get(selected, "billNo")}
              supplierName={get(selected, "supplierName")}
              onDeleted={() => { setDrawerOpen(false); refetch(); }}
              trigger={<Button data-testid="drawer-delete" size="sm" variant="outline" className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /> Delete</Button>}
            />
          ) : (
            <Button data-testid="drawer-delete" size="sm" variant="outline" className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50" onClick={() => setDelOpen(true)}>
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )
        ) : undefined}
      />

      {formCfg && (
        <EntityFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          mode={formMode}
          singular={formCfg.singular}
          fields={formMode === "edit" ? formCfg.editFields : formCfg.createFields}
          initialValues={formMode === "edit" ? editRow : null}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      )}

      {importCfg && (
        <ImportDialog open={importOpen} onOpenChange={setImportOpen} config={importCfg} onDone={refetchList} />
      )}
      {importCfg?.duplicatesEndpoint && (
        <DuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} config={importCfg} onDone={refetchList} />
      )}
    </div>
  );
  }
}
